'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VideoThumbnailProps } from '@/types/hiveshare';
import { 
  generateThumbnailUrls, 
  preloadThumbnails, 
  getOptimizedThumbnailOptions,
  getDeviceOptimizedOptions 
} from '@/utils/hiveShareThumbnails';
import { getResponsiveThumbnailOptions } from '@/config/hiveshare';
import { HiveShareService } from '@/services/hiveShareService';

/**
 * AnimatedThumbnail Component
 * 
 * A React component that displays Cloudflare Stream video thumbnails with:
 * - Signed URL support for authenticated content
 * - Lazy loading support
 * - Responsive sizing
 * - Graceful fallbacks
 */
export const AnimatedThumbnail: React.FC<VideoThumbnailProps> = ({
  videoId,
  title,
  options = {},
  animateOnHover = false, // Disabled for now with signed URLs
  lazy = true,
  className,
  breakpoint = 'desktop',
  fallbackUrl,
  onLoad,
  onError,
  signedToken // New prop for signed URL token
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isAnimated, setIsAnimated] = useState(!animateOnHover);
  const [isIntersecting, setIsIntersecting] = useState(!lazy);
  const [preloadedAnimated, setPreloadedAnimated] = useState(false);
  
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  // Generate thumbnail URLs with device-specific optimizations (for fallback)
  const deviceOptions = getDeviceOptimizedOptions();
  const optimizedOptions = getOptimizedThumbnailOptions(undefined, undefined, breakpoint);
  const mergedOptions = { ...optimizedOptions, ...deviceOptions, ...options };
  const thumbnailUrls = generateThumbnailUrls(videoId, mergedOptions, breakpoint);

  // Performance optimization: disable animation for very slow connections
  const shouldUseAnimation = !deviceOptions.duration || deviceOptions.duration !== '2s' || !animateOnHover;

  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (!lazy || !containerRef.current) return;

    intersectionObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          intersectionObserverRef.current?.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    intersectionObserverRef.current.observe(containerRef.current);

    return () => {
      intersectionObserverRef.current?.disconnect();
    };
  }, [lazy]);

  // Build thumbnail URL using signed token or direct URL
  const getThumbnailUrl = (): string => {
    if (!videoId || !isIntersecting) return '';
    
    // Get responsive sizing
    const responsiveOptions = getResponsiveThumbnailOptions(breakpoint);
    const mergedOptions = { ...responsiveOptions, ...options };
    
    // Build query parameters
    const params = new URLSearchParams();
    if (mergedOptions.time) params.set('time', mergedOptions.time.toString());
    if (mergedOptions.height) params.set('height', mergedOptions.height.toString());
    if (mergedOptions.width) params.set('width', mergedOptions.width.toString());
    if (mergedOptions.fit) params.set('fit', mergedOptions.fit);
    
    const queryString = params.toString();
    const queryParam = queryString ? `?${queryString}` : '';
    
    // Use environment variable or fallback for account ID
    const accountId = process.env.NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_ID || 'ys64orqv99pkve9j';
    const customerDomain = `customer-${accountId}.cloudflarestream.com`;
    
    let finalUrl = '';
    
    if (signedToken) {
      // Check if signedToken is a full URL or just a token
      if (signedToken.startsWith('https://')) {
        // Full signed URL - append query params if any
        finalUrl = queryParam ? `${signedToken}${signedToken.includes('?') ? '&' : '?'}${params.toString()}` : signedToken;
      } else {
        // Token only - build URL with token
        finalUrl = `https://${customerDomain}/${signedToken}/thumbnails/thumbnail.jpg${queryParam}`;
      }
    } else {
      // Fall back to direct URL (may not work for private videos)
      finalUrl = `https://${customerDomain}/${videoId}/thumbnails/thumbnail.jpg${queryParam}`;
    }
    
    return finalUrl;
  };

  // For now, disable hover animation since it requires separate signed URLs
  const handleMouseEnter = () => {
    // Hover animation disabled for signed URLs to avoid multiple API calls
    // Could be implemented later with a separate animated thumbnail signed URL
  };

  const handleMouseLeave = () => {
    // Hover animation disabled for signed URLs
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHasError(true);
    onError?.(event.nativeEvent);
  };

  // Determine which image source to use
  const getImageSrc = () => {
    if (!isIntersecting) return '';
    
    // Priority 1: Fallback URL if there's an error
    if (hasError && fallbackUrl) {
      return fallbackUrl;
    }
    
    // Priority 2: Use constructed thumbnail URL (with signed token if available)
    return getThumbnailUrl();
  };

  const imageSrc = getImageSrc();

  // Don't render if no video ID
  if (!videoId) {
    return (
      <div 
        ref={containerRef}
        className={cn(
          "flex items-center justify-center bg-gray-100 border rounded",
          "w-full h-full",
          className
        )}
      >
        <Video className="h-8 w-8 text-gray-400" />
      </div>
    );
  }

  // Show fallback if no signed token is available for authenticated content
  const showFallback = !signedToken && !fallbackUrl;
  if (showFallback && isIntersecting) {
    return (
      <div 
        ref={containerRef}
        className={cn(
          "flex flex-col items-center justify-center bg-gray-50 border rounded",
          "w-full h-full",
          className
        )}
      >
        <Video className="h-6 w-6 text-gray-400 mb-2" />
        <span className="text-xs text-gray-500 text-center px-2">
          Preview not available
        </span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden rounded border bg-gray-100",
        "w-full h-full",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Loading state */}
      {isIntersecting && !isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Video className="h-8 w-8 text-gray-400 animate-pulse" />
        </div>
      )}

      {/* Error state */}
      {hasError && !fallbackUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <Video className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Failed to load</p>
          </div>
        </div>
      )}

      {/* Main image */}
      {imageSrc && (
        <img
          ref={imageRef}
          src={imageSrc}
          alt={title}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading={lazy ? "lazy" : "eager"}
        />
      )}

      {/* Hover overlay for visual feedback */}
      {isLoaded && (
        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-200" />
      )}
    </div>
  );
};

/**
 * Simplified thumbnail component for cases where you just need a basic thumbnail
 */
export const SimpleThumbnail: React.FC<{
  videoId: string;
  title: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
  signedToken?: string;
}> = ({ 
  videoId, 
  title, 
  className, 
  size = 'md',
  animated = false,
  signedToken
}) => {
  const sizeConfig = {
    sm: { breakpoint: 'mobile' as const, height: 120, width: 160 },
    md: { breakpoint: 'tablet' as const, height: 180, width: 240 },
    lg: { breakpoint: 'desktop' as const, height: 240, width: 320 }
  };

  const config = sizeConfig[size];

  return (
    <AnimatedThumbnail
      videoId={videoId}
      title={title}
      className={className}
      breakpoint={config.breakpoint}
      animateOnHover={animated}
      signedToken={signedToken}
      options={{
        height: config.height,
        width: config.width
      }}
    />
  );
};

export default AnimatedThumbnail;